import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  AllowanceEntity,
  BokkzTslDataService,
  Kind,
  OpvangPeriodes,
  OpvangPeriodesOpvangVorm,
  OpvangPeriodesSoortOpvang
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {filter, Observable, shareReplay, take} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {SharedUiAlertModule} from "@snelstart/ui-alert";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type PeriodeFormGroup = FormGroup<{
  begindatum: FormControl<Date | null>;
  einddatum: FormControl<Date | null>;
}>;

type OpvangPeriodeFormGroup = FormGroup<{
  identificatienummerInstelling: FormControl<string | null>;
  naamKinderopvanginstelling: FormControl<string | null>;
  periodeOpvang: PeriodeFormGroup;
  opvangVorm: FormControl<OpvangPeriodesOpvangVorm | null>;
  soortOpvang: FormControl<OpvangPeriodesSoortOpvang | null>;
  urenPerMaand: FormControl<number | null>;
  uurprijs: FormControl<number | null>;
}>;

type KindFormGroup = FormGroup<{
  bsn: FormControl<string | null>;
  voornaam: FormControl<string | null>;
  geboortedatum: FormControl<Date | null>;
  opvangPerKind: FormArray<OpvangPeriodeFormGroup>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-kinderopvangtoeslag',
  templateUrl: './kinderopvangtoeslag.component.html',
  styleUrls: ['./kinderopvangtoeslag.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule,
    SharedUiAlertModule
  ]
})
export class KinderopvangtoeslagComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzTslDataService);
  private destroyRef = inject(DestroyRef);
  public hasKinderen = false;
  public year: number | undefined;

  public formGroup = new FormGroup({
    kinderen: new FormArray<KindFormGroup>([])
  });

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];

  public opvangVorm: SelectFormControlItem<string>[] = [
    {value: OpvangPeriodesOpvangVorm.GASTOUDEROPVANG, label: "Gastouderopvang"},
    {value: OpvangPeriodesOpvangVorm.KINDERCENTRUM, label: "Kindcentrum"},
  ];

  public opvangSoort: SelectFormControlItem<string>[] = [
    {value: OpvangPeriodesSoortOpvang.BUITENSCHOOLSE_OPVANG, label: "Buitenschoolse opvang"},
    {value: OpvangPeriodesSoortOpvang.DAGOPVANG, label: "Dagopvang"},
  ];

  readonly allowanceEntity$: Observable<AllowanceEntity> =
    this.dataService.allowanceEntity$.pipe(
      filter((data): data is AllowanceEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public ngOnInit(): void {
    this.allowanceEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.year = data.year;
      this.hasKinderen = (data.toeslagen?.kinderen?.length ?? 0) > 0;
      this.updateForm(data.toeslagen?.kinderen ?? []);
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.dataService.calculateLatestAllowances().subscribe();
  }

  get kinderen(): FormArray<KindFormGroup> {
    return this.formGroup.controls.kinderen;
  }

  getOpvangPeriodes(kindIndex: number): FormArray<OpvangPeriodeFormGroup> {
    return this.kinderen.at(kindIndex).controls.opvangPerKind;
  }

  public isJongerDan13Jaar(geboortedatum: Date | null | undefined): boolean {
    if (!this.year || !geboortedatum) {
      return false;
    }
    const yearThreshold = this.year - 13;
    const birthYear = new Date(geboortedatum).getFullYear();
    return birthYear >= yearThreshold;
  }

  addRij(kindIndex: number): void {
    this.getOpvangPeriodes(kindIndex).push(this.createOpvangGroup(), {emitEvent: false});
  }

  verwijderRij(kindIndex: number, periodeIndex: number): void {
    this.getOpvangPeriodes(kindIndex).removeAt(periodeIndex, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  private createKindGroup(kind: Kind): KindFormGroup {
    return new FormGroup({
      bsn: new FormControl<string | null>(kind.bsn ?? null),
      voornaam: new FormControl<string | null>(kind.voornaam ?? null),
      geboortedatum: new FormControl<Date | null>(kind.geboortedatum ?? null),
      opvangPerKind: new FormArray<OpvangPeriodeFormGroup>((kind.opvangPerKind ?? []).map(p => this.createOpvangGroup(p))),
    }) as KindFormGroup;
  }

  private createOpvangGroup(item?: OpvangPeriodes): OpvangPeriodeFormGroup {
    return new FormGroup({
      identificatienummerInstelling: new FormControl<string | null>(item?.identificatienummerInstelling ?? null),
      naamKinderopvanginstelling: new FormControl<string | null>(item?.naamKinderopvanginstelling ?? null),
      opvangVorm: new FormControl<OpvangPeriodesOpvangVorm | null>(item?.opvangVorm ?? null),
      periodeOpvang: new FormGroup({
        begindatum: new FormControl<Date | null>(item?.periodeOpvang?.begindatum ?? null),
        einddatum: new FormControl<Date | null>(item?.periodeOpvang?.einddatum ?? null),
      }),
      soortOpvang: new FormControl<OpvangPeriodesSoortOpvang | null>(item?.soortOpvang ?? null),
      urenPerMaand: new FormControl<number | null>(item?.urenPerMaand ?? null),
      uurprijs: new FormControl<number | null>(item?.uurprijs ?? null),
    }) as OpvangPeriodeFormGroup;
  }

  private updateForm(kinderen: Kind[]): void {
    const fa = this.kinderen;

    while (fa.length > kinderen.length) {
      fa.removeAt(fa.length - 1, {emitEvent: false});
    }

    kinderen.forEach((kind, i) => {
      if (i >= fa.length) {
        fa.push(this.createKindGroup(kind), {emitEvent: false});
        return;
      }

      const kindGroup = fa.at(i);
      kindGroup.patchValue(
        {
          bsn: kind.bsn ?? null,
          voornaam: kind.voornaam ?? null,
          geboortedatum: kind.geboortedatum ?? null,
        },
        {emitEvent: false},
      );

      const periodes = kind.opvangPerKind ?? [];
      const periodesFa = kindGroup.controls.opvangPerKind;

      while (periodesFa.length > periodes.length) {
        periodesFa.removeAt(periodesFa.length - 1, {emitEvent: false});
      }

      periodes.forEach((periode, j) => {
        if (j < periodesFa.length) {
          periodesFa.at(j).patchValue(periode, {emitEvent: false});
        } else {
          periodesFa.push(this.createOpvangGroup(periode), {emitEvent: false});
        }
      });
    });
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});

    setTimeout(() => {
      const edited = this.formGroup.getRawValue().kinderen ?? [];

      this.dataService.patchAllowanceEntity((currentEntity) => {
        const existing = currentEntity.toeslagen?.kinderen ?? [];

        const merged: Kind[] = existing.map((kind, i) => {
          const match =
            (kind.bsn && edited.find(e => e.bsn && e.bsn === kind.bsn)) ??
            edited[i];

          if (!match) {
            return kind;
          }

          const opvangPerKind = (match.opvangPerKind ?? []).map((periode) => {
            const cleaned = Object.fromEntries(
              Object.entries(periode).filter(([, v]) => v !== null)) as Partial<OpvangPeriodes>;

            if (periode.periodeOpvang) {
              cleaned.periodeOpvang = Object.fromEntries(
                Object.entries(periode.periodeOpvang).filter(([, v]) => v !== null),
              ) as OpvangPeriodes['periodeOpvang'];
            }

            return cleaned as OpvangPeriodes;
          });

          return {...kind, opvangPerKind};
        });

        return {
          ...currentEntity,
          toeslagen: {...currentEntity.toeslagen, kinderen: merged},
        };
      });
    }, 0);
  }
}
